import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourStepsRail from '../../components/harbour/HarbourStepsRail';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourOptionGroup from '../../components/harbour/HarbourOptionGroup';
import { HarbourField } from '../../components/harbour/HarbourField';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import {
  apiHarbourStartSenPathway,
  apiHarbourInviteFamily,
  apiHarbourRemoveParent,
} from '../../utils/rmsApi';
import { senPathwayEligibility } from '../../utils/harbourPupils';
import { SCHOOL_SEN_CONSENT_VERSION } from '../../constants/consentVersions';
import '../../components/harbour/harbour.css';

/*
 * Add to SEND pathway (TDI-53 section 03, frames 3192:116778 / 3915:253681).
 *   1. Confirm consent — Yes or No only (review query 08: no "Not sure").
 *      "No" says so at once and the pathway cannot be started; nothing is
 *      written.
 *   2. Parent or carer details — only when the pupil does not have exactly
 *      one parent or carer on record: confirm who is responsible (or add
 *      them), so the home side of the profile has someone to ask. With one
 *      on record, consent is the single step, as drawn.
 *   Submitting starts the pathway and then invites the parent or carer, in
 *   that order, so the invite email carries the SEND wording.
 *
 * The consent question wording is under TDI-55 (Jasmine / IG); the options
 * below are the designed labels. Do not reword without that sign-off.
 */
const STEPS = ['Confirm consent', 'Parent or carer details'];

const CONSENT_OPTIONS = [
  { value: 'yes', label: 'Yes - appropriate consent has been given' },
  { value: 'no', label: 'No' },
];
const NO_CONSENT_LINE = "You must have the parent or carer's consent to proceed";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function relationshipLabel(id) {
  const labels = {
    parent: 'Parent or carer',
    step_parent: 'Step-parent',
    foster_carer: 'Foster carer',
    kinship_carer: 'Kinship carer',
    legal_guardian: 'Legal guardian',
    informal_carer: 'Informal carer',
  };
  return labels[id] || null;
}

export default function HarbourAddToSenFlyout({ pupil, orgId, onClose, onStarted }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [extraParents, setExtraParents] = useState([]);
  const [addingParent, setAddingParent] = useState(false);
  const [newParent, setNewParent] = useState({ fullName: '', relationship: '', email: '', phone: '' });
  const [newParentErrors, setNewParentErrors] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const firstName = pupil ? pupil.name.split(' ')[0] : 'the pupil';

  // Pilot eligibility (TDI-488): the SEND pathway is Key Stage 1 and 2 only.
  // An ineligible pupil gets the reason, not a form that fails at the end.
  // Interim wording pending Jasmine's copy.
  const eligibility = senPathwayEligibility(pupil);
  const ineligibleReason = {
    BELOW_MINIMUM_YEAR:
      `The SEND pathway is for pupils in Year 1 to Year 6, so it is not available while ${firstName} is in Reception.`,
    ABOVE_MAXIMUM_YEAR:
      `The SEND pathway is for pupils in Year 1 to Year 6 (Key Stages 1 and 2), so it is not available for ${firstName}.`,
    YEAR_GROUP_MISSING:
      `Add ${firstName}'s year group to their record first, so we can check the SEND pathway is right for them.`,
  }[eligibility.reasonCode]
    || `The SEND pathway is for pupils in Year 1 to Year 6 (Key Stages 1 and 2), so it is not available for ${firstName}.`;

  const parentOptions = useMemo(() => {
    const onRecord = (pupil?.guardians || [])
      .filter((g) => g.status !== 'superseded')
      .map((g) => ({
        value: g.participantId || g.id,
        label: [g.firstName, g.lastName].filter(Boolean).join(' ') || g.email,
        meta: [relationshipLabel(g.relationship) || 'Parent or carer', g.phone, g.email].filter(Boolean).join(' · '),
        removable: g.status !== 'account_linked',
        linked: g.status === 'account_linked',
      }));
    return [...onRecord, ...extraParents];
  }, [pupil, extraParents]);

  const selectedParent = parentId || (parentOptions[0] && parentOptions[0].value) || null;
  // One parent or carer on record: nothing to choose, so consent is the only step.
  const singleStep = parentOptions.length === 1;

  const next = () => {
    if (!answer) {
      setError('Choose an answer to continue');
      return;
    }
    if (answer !== 'yes') return;
    setError(null);
    if (singleStep) {
      submit();
      return;
    }
    setStep(1);
  };

  const saveNewParent = () => {
    const errs = {};
    const [first, ...rest] = newParent.fullName.trim().split(/\s+/);
    if (!first || rest.length === 0) errs.fullName = "Enter the parent or carer's full name";
    if (!EMAIL_RE.test(newParent.email.trim())) errs.email = "Enter the parent or carer's email address";
    setNewParentErrors(errs);
    if (Object.keys(errs).length) return;
    const value = `new-${Date.now()}`;
    setExtraParents((list) => [
      ...list,
      {
        value,
        label: newParent.fullName.trim(),
        meta: [relationshipLabel(newParent.relationship) || 'Parent or carer', newParent.phone.trim(), newParent.email.trim()].filter(Boolean).join(' · '),
        payload: {
          firstName: first,
          lastName: rest.join(' '),
          email: newParent.email.trim(),
          phone: newParent.phone.trim() || undefined,
          relationship: newParent.relationship || 'parent',
        },
      },
    ]);
    setParentId(value);
    setAddingParent(false);
    setNewParent({ fullName: '', relationship: '', email: '', phone: '' });
  };

  const removeParent = async (option) => {
    setError(null);
    if (String(option.value).startsWith('new-')) {
      setExtraParents((list) => list.filter((p) => p.value !== option.value));
      if (parentId === option.value) setParentId(null);
      return;
    }
    try {
      await apiHarbourRemoveParent({ orgId, patientRecordId: pupil.id, participantId: option.value });
      if (parentId === option.value) setParentId(null);
      if (onStarted) await onStarted(); // refresh participants
    } catch (e) {
      setError(e?.message || 'The parent or carer could not be removed.');
    }
  };

  const submit = async () => {
    setError(null);
    const chosen = parentOptions.find((p) => p.value === selectedParent);
    if (!chosen) return setError("Add the parent or carer's details to continue");
    setBusy(true);
    try {
      // Start the pathway first: the invite email picks its SEN wording from
      // the record state, so the pathway must be active before the send.
      await apiHarbourStartSenPathway({
        orgId, patientRecordId: pupil.id, consentConfirmed: true,
        // Which consent wording this wizard showed (TDI-480).
        consentVersion: SCHOOL_SEN_CONSENT_VERSION,
      });
      if (chosen.payload) {
        await apiHarbourInviteFamily({ orgId, patientRecordId: pupil.id, parent: chosen.payload });
      } else if (!chosen.linked) {
        await apiHarbourInviteFamily({ orgId, patientRecordId: pupil.id });
      }
      if (onStarted) await onStarted();
      navigate(`/rms/pupils/${pupil.id}`, {
        replace: true,
        state: { harbourAlert: `${firstName} added to the SEND pathway` },
      });
    } catch (e) {
      setError(e?.message || 'That did not work. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
    return null;
  };

  if (!eligibility.eligible) {
    return (
      <HarbourFlyout
        width="wide"
        title={pupil?.name || ''}
        year={pupil?.yearGroup}
        eyebrow={{ tone: 'sen', label: 'Add to SEND pathway' }}
        onClose={onClose}
      >
        <div className="harbour-task-body">
          <div className="harbour-task-body__main">
            <div className="harbour-form-card">
              <h2 className="harbour-form-card__title">{`The SEND pathway is not available for ${firstName}`}</h2>
              <HarbourNotice kind="warning" title={ineligibleReason}>
                {`This does not affect the support available to ${firstName} through your school's usual SEND processes.`}
              </HarbourNotice>
              <div className="harbour-form-footer">
                <HarbourButton kind="primary" onClick={onClose}>Close</HarbourButton>
              </div>
            </div>
          </div>
          <aside className="harbour-task-body__aside" />
        </div>
      </HarbourFlyout>
    );
  }

  return (
    <HarbourFlyout
      width="wide"
      title={pupil?.name || ''}
      year={pupil?.yearGroup}
      eyebrow={{ tone: 'sen', label: 'Add to SEND pathway' }}
      onClose={onClose}
    >
      <div className="harbour-task-body">
        <div className="harbour-task-body__steps">
          <HarbourStepsRail tone="sen" steps={singleStep ? STEPS.slice(0, 1) : STEPS} currentIndex={step} />
        </div>
        <div className="harbour-task-body__main">
          <div className="harbour-form-card">
            {step === 0 && (
              <>
                <div>
                  <h2 className="harbour-form-card__title">Confirm consent</h2>
                  <p className="harbour-form-card__hint">
                    {`Before you add ${firstName}, confirm that their parent or carer has agreed to this.`}
                  </p>
                </div>
                <HarbourOptionGroup
                  ariaLabel="Confirm consent"
                  tone="sen"
                  options={CONSENT_OPTIONS}
                  value={answer}
                  onChange={(v) => { setAnswer(v); setError(null); }}
                />
                {answer === 'no' && (
                  <HarbourNotice kind="warning" title={NO_CONSENT_LINE} />
                )}
                {error && (
                  <HarbourNotice kind="error" title={answer ? 'That did not work' : 'Consent needed'}>
                    {error}
                  </HarbourNotice>
                )}
                <div className="harbour-form-footer">
                  <HarbourButton kind="tertiary" onClick={onClose}>Cancel</HarbourButton>
                  {singleStep ? (
                    <HarbourButton kind="primary" leadingIcon="plus" busy={busy} disabled={answer === 'no'} onClick={next}>
                      Add to SEND pathway
                    </HarbourButton>
                  ) : (
                    <HarbourButton kind="primary" trailingIcon="arrow-right" disabled={answer === 'no'} onClick={next}>
                      Continue
                    </HarbourButton>
                  )}
                </div>
              </>
            )}
            {step === 1 && !addingParent && (
              <>
                <div>
                  <h2 className="harbour-form-card__title">Parent or carer details</h2>
                  <p className="harbour-form-card__hint">
                    Please provide the details of a parent or carer with legal responsibility for the child:
                  </p>
                </div>
                <HarbourOptionGroup
                  ariaLabel="Responsible parent or carer"
                  tone="sen"
                  options={parentOptions.map((p) => ({
                    ...p,
                    trailing: p.removable !== false && parentOptions.length > 1 ? (
                      <HarbourTextLink onClick={(e) => { e.stopPropagation(); removeParent(p); }}>
                        Remove
                      </HarbourTextLink>
                    ) : null,
                  }))}
                  value={selectedParent}
                  onChange={setParentId}
                />
                <div>
                  <HarbourButton kind="secondary" leadingIcon="plus" onClick={() => setAddingParent(true)}>
                    Add a parent / carer
                  </HarbourButton>
                </div>
                {error && <HarbourNotice kind="error" title="That did not work">{error}</HarbourNotice>}
                <div className="harbour-form-footer">
                  <HarbourButton kind="secondary" onClick={() => { setError(null); setStep(0); }}>Back</HarbourButton>
                  <HarbourButton kind="primary" leadingIcon="plus" busy={busy} onClick={submit}>
                    Add to SEND pathway
                  </HarbourButton>
                </div>
              </>
            )}
            {step === 1 && addingParent && (
              <>
                <h2 className="harbour-form-card__title">Add Parent / Carer</h2>
                <HarbourField label="Full name" value={newParent.fullName} onChange={(e) => setNewParent((p) => ({ ...p, fullName: e.target.value }))} error={newParentErrors.fullName} />
                <HarbourField as="select" label={`Relationship to ${firstName}`} value={newParent.relationship} onChange={(e) => setNewParent((p) => ({ ...p, relationship: e.target.value }))}>
                  <option value="" />
                  <option value="parent">Parent or carer</option>
                  <option value="step_parent">Step-parent</option>
                  <option value="foster_carer">Foster carer</option>
                  <option value="kinship_carer">Kinship carer</option>
                  <option value="legal_guardian">Legal guardian</option>
                </HarbourField>
                <HarbourField
                  label="Email address"
                  hint="We send the sign in email to this address"
                  type="email"
                  value={newParent.email}
                  onChange={(e) => setNewParent((p) => ({ ...p, email: e.target.value }))}
                  error={newParentErrors.email}
                />
                <HarbourField label="Phone number" type="tel" value={newParent.phone} onChange={(e) => setNewParent((p) => ({ ...p, phone: e.target.value }))} />
                <div className="harbour-form-footer">
                  <HarbourButton kind="tertiary" onClick={() => setAddingParent(false)}>Cancel</HarbourButton>
                  <HarbourButton kind="primary" onClick={saveNewParent}>Save</HarbourButton>
                </div>
              </>
            )}
          </div>
        </div>
        <aside className="harbour-task-body__aside" />
      </div>
    </HarbourFlyout>
  );
}
